/**
 * localStorage utilities for AI Twin conversations
 * Backend-ready: Will switch to API when backend is available
 */

import { createGetApiHook, createPostApiHook } from "../apiHooks";
import { APIResponseType } from "../../../types/generics/APIResponse.type";

export interface AITwinMessage {
  id: string;
  text: string;
  sender: "user" | "ai";
  timestamp: Date | string;
  run_id?: string | null;
  // Optional confidence score for AI responses (0-1 range, provided by backend)
  confidence?: number | null;
  // True when this message is a real reply written by the talent
  // (coming from talent_response for a given run_id).
  fromTalent?: boolean;
  isVoice?: boolean;
  voiceUrl?: string;
  duration?: number;
}

export interface AITwinConversation {
  id: string;
  talentId: number;
  userId: number;
  userName: string;
  userAvatar?: string;
  messages?: AITwinMessage[]; // Optional - not included in list API response
  lastMessageAt: Date | string;
  createdAt: Date | string;
  thread_id?: string; // Thread ID from API
  lastMessagePreview?: string; // Optional preview text for list views
}

/**
 * Conversation list item from API (without messages)
 * This matches the API response structure for listing conversations
 */
export interface AITwinConversationListItem {
  thread_id: string;
  user_id: number;
  last_id: number;
  last_message_at: string;
  user: {
    id: number;
    first_name: string;
    last_name: string;
    profile_image: string;
    username: string;
  };
}

/**
 * Message item from API when fetching conversation by thread_id
 * API returns: { message: "Success", data: [{ thread_id, message, response, audio_url, created_at, user: {...}, talent: {...} }] }
 * Each item represents a message pair: user message + AI response
 */
export interface AITwinMessageItem {
  thread_id: string;
  message: string; // User's message
  response: string; // AI's response
  audio_url: string;
  run_id?: string | null;
  // Optional real talent reply associated with this run
  talent_response?: string | null;
  // Optional confidence score for AI response (0-1 range) from backend
  confidence?: number | null;
  created_at: string;
  user: {
    id: number;
    first_name: string;
    last_name: string;
    username: string;
    profile_image: string;
    account_type: string;
    bio: string;
    location: string;
    contact_peference: string;
    availability: number;
    referral_point: number;
  };
  talent: {
    id: number;
    username: string;
    profile_image: string;
    cover_image?: string;
    bio: string;
    type: string;
    [key: string]: any; // Allow other talent properties
  };
}

export interface InterruptAITwinPayload {
  thread_id: string;
  /** API expects string; send "" if not available */
  run_id?: string | null;
  talent_response: string;
  /** CTP context: business ID of the CTP performing the interrupt (required when CTP interrupts) */
  business_id?: number | null;
}

export interface InterruptAITwinResponse {
  message: string;
  data?: any;
}

const STORAGE_KEY_PREFIX = "ai_twin_conversations_";

/**
 * Get localStorage key for a specific talent's conversations
 */
const getStorageKey = (talentId: number): string => {
  return `${STORAGE_KEY_PREFIX}${talentId}`;
};

/**
 * Get all conversations for a talent from localStorage
 */
export const getAITwinConversationsFromStorage = (
  talentId: number,
): AITwinConversation[] => {
  try {
    const key = getStorageKey(talentId);
    const stored = localStorage.getItem(key);
    if (stored) {
      const conversations = JSON.parse(stored) as AITwinConversation[];
      // Convert date strings back to Date objects
      return conversations.map((conv) => ({
        ...conv,
        lastMessageAt: new Date(conv.lastMessageAt),
        createdAt: new Date(conv.createdAt),
        messages:
          conv.messages && Array.isArray(conv.messages)
            ? conv.messages.map((msg) => ({
                ...msg,
                timestamp: new Date(msg.timestamp),
              }))
            : [],
      }));
    }
  } catch (error) {
    console.error(
      "Error reading AI Twin conversations from localStorage:",
      error,
    );
  }
  return [];
};

/**
 * Save a conversation to localStorage
 */
export const saveAITwinConversationToStorage = (
  talentId: number,
  conversation: AITwinConversation,
): void => {
  try {
    const key = getStorageKey(talentId);
    const existing = getAITwinConversationsFromStorage(talentId);

    // Check if conversation already exists
    const existingIndex = existing.findIndex((c) => c.id === conversation.id);

    let updated: AITwinConversation[];
    if (existingIndex >= 0) {
      // Update existing conversation
      updated = [...existing];
      updated[existingIndex] = conversation;
    } else {
      // Add new conversation
      updated = [...existing, conversation];
    }

    // Sort by last message time (newest first)
    updated.sort((a, b) => {
      const timeA = new Date(a.lastMessageAt).getTime();
      const timeB = new Date(b.lastMessageAt).getTime();
      return timeB - timeA;
    });

    localStorage.setItem(key, JSON.stringify(updated));
  } catch (error) {
    console.error("Error saving AI Twin conversation to localStorage:", error);
  }
};

/**
 * Add a message to an existing conversation or create a new one
 */
export const addMessageToAITwinConversation = (
  talentId: number,
  userId: number,
  userName: string,
  userAvatar: string | undefined,
  message: AITwinMessage,
): AITwinConversation => {
  const conversations = getAITwinConversationsFromStorage(talentId);

  // Find existing conversation with this user
  let conversation = conversations.find((c) => c.userId === userId);

  if (!conversation) {
    // Create new conversation
    conversation = {
      id: `conv_${talentId}_${userId}_${Date.now()}`,
      talentId,
      userId,
      userName,
      userAvatar,
      messages: [],
      lastMessageAt: new Date(),
      createdAt: new Date(),
    };
  }

  // Add message
  if (!conversation.messages) {
    conversation.messages = [];
  }
  conversation.messages.push(message);
  conversation.lastMessageAt = new Date();

  // Save updated conversation
  saveAITwinConversationToStorage(talentId, conversation);

  return conversation;
};

/**
 * Get user questions (messages sent by users) from conversations
 */
export const getUserQuestionsFromConversations = (
  conversations: AITwinConversation[],
): Array<{
  conversationId: string;
  userId: number;
  userName: string;
  userAvatar?: string;
  question: string;
  timestamp: Date;
  aiResponse?: string;
}> => {
  const questions: Array<{
    conversationId: string;
    userId: number;
    userName: string;
    userAvatar?: string;
    question: string;
    timestamp: Date;
    aiResponse?: string;
  }> = [];

  conversations.forEach((conv) => {
    if (!conv.messages || !Array.isArray(conv.messages)) return;
    conv.messages.forEach((msg, index) => {
      if (msg.sender === "user") {
        // Find the AI response that follows this question
        const aiResponse =
          conv.messages && conv.messages[index + 1]?.sender === "ai"
            ? conv.messages[index + 1].text
            : undefined;

        questions.push({
          conversationId: conv.id,
          userId: conv.userId,
          userName: conv.userName,
          userAvatar: conv.userAvatar,
          question: msg.text,
          timestamp: new Date(msg.timestamp),
          aiResponse,
        });
      }
    });
  });

  // Sort by timestamp (newest first)
  questions.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

  return questions;
};

/**
 * Clear all conversations for a talent (for testing)
 */
export const clearAITwinConversations = (talentId: number): void => {
  try {
    const key = getStorageKey(talentId);
    localStorage.removeItem(key);
  } catch (error) {
    console.error("Error clearing AI Twin conversations:", error);
  }
};

/**
 * Generate dummy conversations for testing/demo purposes
 */
export const generateDummyConversations = (
  talentId: number,
): AITwinConversation[] => {
  const now = new Date();
  const dummyUsers = [
    { id: 200001, name: "Alex Johnson", avatar: undefined },
    { id: 200002, name: "Sarah Chen", avatar: undefined },
    { id: 200003, name: "Michael Park", avatar: undefined },
    { id: 200004, name: "Emma Wilson", avatar: undefined },
    { id: 200005, name: "David Kim", avatar: undefined },
  ];

  const sampleQuestions = [
    "What are your upcoming events?",
    "Can you tell me about your latest release?",
    "How can I book a session with you?",
    "What's included in your premium content?",
    "Do you offer virtual meet and greets?",
    "What's your favorite song to perform?",
    "Can you share some behind-the-scenes content?",
    "How do I become a member?",
  ];

  const sampleResponses = [
    "I have several exciting events coming up! Check out my events page for details.",
    "My latest release is available on all major platforms. Thank you for your interest!",
    "You can book a session through my profile. I'd love to connect with you!",
    "Premium content includes exclusive videos, photos, and personalized messages.",
    "Yes! I offer virtual meet and greets. Book a session to schedule one.",
    "That's a tough question! I love performing all my songs, but my current favorite is...",
    "I share exclusive behind-the-scenes content with my premium members!",
    "You can become a member by subscribing to my premium content or joining my fan club.",
  ];

  return dummyUsers.map((user, userIndex) => {
    const messageCount = Math.floor(Math.random() * 5) + 2; // 2-6 messages per conversation
    const messages: AITwinMessage[] = [];

    for (let i = 0; i < messageCount; i++) {
      const questionIndex =
        (userIndex * messageCount + i) % sampleQuestions.length;
      const timestamp = new Date(now.getTime() - (messageCount - i) * 3600000); // Messages spaced 1 hour apart

      // User message
      messages.push({
        id: `msg_${talentId}_${user.id}_${i}_user`,
        text: sampleQuestions[questionIndex],
        sender: "user",
        timestamp,
      });

      // AI response
      messages.push({
        id: `msg_${talentId}_${user.id}_${i}_ai`,
        text: sampleResponses[questionIndex],
        sender: "ai",
        timestamp: new Date(timestamp.getTime() + 60000), // AI responds 1 minute later
      });
    }

    const lastMessageTime = messages[messages.length - 1].timestamp;
    const lastMessageDate =
      typeof lastMessageTime === "string"
        ? new Date(lastMessageTime)
        : lastMessageTime;
    const createdAt = new Date(
      lastMessageDate.getTime() - messageCount * 3600000,
    );

    return {
      id: `conv_${talentId}_${user.id}_dummy`,
      talentId,
      userId: user.id,
      userName: user.name,
      userAvatar: user.avatar,
      messages,
      lastMessageAt: lastMessageTime,
      createdAt,
    };
  });
};

/**
 * Backend API endpoint for getting conversations
 * GET /api/v1/talents/list/conversations?limit=10&offset=0&talent_id={talentId}[&business_id=...][&ait_card_request_id=...]
 * talent_id is REQUIRED. business_id and ait_card_request_id optional (CTP context).
 */
const getAITwinConversationsEndpoint = ({
  talentId,
  limit = 10,
  offset = 0,
  business_id,
  ait_card_request_id,
}: {
  talentId: number;
  limit?: number;
  offset?: number;
  business_id?: number | string | null;
  ait_card_request_id?: number | string | null;
}) => {
  // Validate that talentId is provided and valid
  if (!talentId || talentId <= 0) {
    throw new Error("talent_id is required and must be a positive number");
  }

  const params = new URLSearchParams();
  params.set("talent_id", String(talentId)); // Required parameter
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  if (business_id != null && business_id !== "")
    params.set("business_id", String(business_id));
  if (ait_card_request_id != null && ait_card_request_id !== "")
    params.set("ait_card_request_id", String(ait_card_request_id));
  return `/talents/list/conversations?${params.toString()}`;
};

/**
 * Backend API hook for listing conversations
 * API returns: { message: "Success", data: [{ thread_id, user_id, last_id, last_message_at, user: {...} }] }
 * We need to transform this to AITwinConversation format
 */
const useGetAITwinConversationsBackendRaw = createGetApiHook<
  APIResponseType<AITwinConversationListItem[]>,
  {
    talentId: number;
    limit?: number;
    offset?: number;
    business_id?: number | string | null;
    ait_card_request_id?: number | string | null;
  }
>(getAITwinConversationsEndpoint);

/**
 * Transform API response to AITwinConversation format
 */
const useGetAITwinConversationsBackend = (options: {
  key: any[];
  params: {
    talentId: number;
    limit?: number;
    offset?: number;
    business_id?: number | string | null;
    ait_card_request_id?: number | string | null;
  };
  options?: any;
}) => {
  const result = useGetAITwinConversationsBackendRaw({
    key: options.key,
    params: options.params,
    options: options.options,
  });

  // Transform API response to AITwinConversation format
  const transformedData: APIResponseType<AITwinConversation[]> | undefined =
    result.data?.data
      ? {
          ...result.data,
          data: result.data.data.map((item: AITwinConversationListItem) => {
            const lastMessagePreview =
              (item as any).last_message || (item as any).message || "";

            // Some backends may not populate user_id field, but user.id is always present.
            // Prefer explicit user_id, then nested user.id, otherwise fallback to 0.
            const userId = (item as any).user_id ?? item.user?.id ?? 0;

            const firstName = item.user?.first_name || "";
            const lastName = item.user?.last_name || "";
            const fullName = `${firstName} ${lastName}`.trim();

            return {
              id: item.thread_id, // Use thread_id as id
              thread_id: item.thread_id,
              talentId: options.params.talentId,
              userId,
              userName: fullName || item.user?.username || "User",
              userAvatar: item.user?.profile_image,
              // Messages not included in list response - will be fetched when thread is clicked
              messages: undefined,
              lastMessageAt: item.last_message_at,
              // Use last_message_at as createdAt if not provided
              createdAt: item.last_message_at,
              lastMessagePreview,
            } as AITwinConversation;
          }) as AITwinConversation[],
        }
      : undefined;

  return {
    ...result,
    data: transformedData,
  };
};

/**
 * Backend API endpoint for CTP: business conversations
 * GET /api/v1/talents/list/business-conversations?limit=10&offset=0&business_id=...&ait_card_request_id=...[&user_id=...]
 * Used when fetching conversations for a CTP with business_id and ait_card_request_id.
 */
const getBusinessConversationsEndpoint = ({
  limit = 10,
  offset = 0,
  business_id,
  ait_card_request_id,
  user_id,
}: {
  limit?: number;
  offset?: number;
  business_id?: number | string | null;
  ait_card_request_id?: number | string | null;
  user_id?: number | string | null;
}) => {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  if (business_id != null && business_id !== "")
    params.set("business_id", String(business_id));
  if (ait_card_request_id != null && ait_card_request_id !== "")
    params.set("ait_card_request_id", String(ait_card_request_id));
  if (user_id != null && user_id !== "") params.set("user_id", String(user_id));
  return `/talents/list/business-conversations?${params.toString()}`;
};

const useGetBusinessConversationsBackendRaw = createGetApiHook<
  APIResponseType<AITwinConversationListItem[]>,
  {
    limit?: number;
    offset?: number;
    business_id?: number | string | null;
    ait_card_request_id?: number | string | null;
    user_id?: number | string | null;
  }
>(getBusinessConversationsEndpoint);

/**
 * CTP: Fetch conversations via business-conversations endpoint.
 * Pass talentId when on monitor page so conversations get the correct talentId for UI.
 */
export const useGetBusinessConversations = (options: {
  key: any[];
  params: {
    limit?: number;
    offset?: number;
    business_id?: number | string | null;
    ait_card_request_id?: number | string | null;
    user_id?: number | string | null;
    talentId?: number;
  };
  options?: any;
}) => {
  const { key, params, options: queryOptions } = options;
  const { talentId, ...apiParams } = params;
  const result = useGetBusinessConversationsBackendRaw({
    key,
    params: apiParams,
    options: queryOptions,
  });

  const transformedData: APIResponseType<AITwinConversation[]> | undefined =
    result.data?.data
      ? {
          ...result.data,
          data: result.data.data.map((item: AITwinConversationListItem) => {
            const lastMessagePreview =
              (item as any).last_message ?? (item as any).message ?? "";
            const userId = (item as any).user_id ?? item.user?.id ?? 0;
            const firstName = item.user?.first_name || "";
            const lastName = item.user?.last_name || "";
            const fullName = `${firstName} ${lastName}`.trim();
            return {
              id: item.thread_id,
              thread_id: item.thread_id,
              talentId: talentId ?? 0,
              userId,
              userName: fullName || item.user?.username || "User",
              userAvatar: item.user?.profile_image,
              messages: undefined,
              lastMessageAt: item.last_message_at,
              createdAt: item.last_message_at,
              lastMessagePreview,
            } as AITwinConversation;
          }) as AITwinConversation[],
        }
      : undefined;

  return {
    ...result,
    data: transformedData,
  };
};

/**
 * Backend API endpoint for getting a single conversation by thread_id
 * GET /api/v1/talents/list/conversations/{thread_id}
 */
const getAITwinConversationByThreadIdEndpoint = ({
  threadId,
}: {
  threadId: string;
}) => `/talents/list/conversations/${threadId}`;

/**
 * Backend API hook for getting messages by thread_id (raw)
 * API returns: { message: "Success", data: [AITwinMessageItem[]] }
 */
const useGetAITwinConversationByThreadIdRaw = createGetApiHook<
  APIResponseType<AITwinMessageItem[]>,
  { threadId: string }
>(getAITwinConversationByThreadIdEndpoint);

/**
 * Transform API response to AITwinConversation format
 * API returns array of message objects, we need to transform to conversation with messages array
 */
export const useGetAITwinConversationByThreadId = (options: {
  key: any[];
  params: { threadId: string };
  options?: any;
}) => {
  const result = useGetAITwinConversationByThreadIdRaw({
    key: options.key,
    params: options.params,
    options: options.options,
  });

  // Transform API response (array of message pairs) to AITwinConversation format
  // Each item in the array represents one exchange: user message + AI response
  const transformedData: APIResponseType<AITwinConversation> | undefined =
    result.data?.data
      ? (() => {
          const messagePairs = result.data.data;

          // Validate that messagePairs is an array
          if (
            !messagePairs ||
            !Array.isArray(messagePairs) ||
            messagePairs.length === 0
          ) {
            return undefined;
          }

          // Get conversation metadata from first and last message pairs
          const firstPair = messagePairs[0];
          const lastPair = messagePairs[messagePairs.length - 1];

          // Validate firstPair exists before accessing its properties
          if (!firstPair || !lastPair) {
            return undefined;
          }

          // Transform message pairs to AITwinMessage format
          // Each pair becomes: [user message, AI response]
          const transformedMessages: AITwinMessage[] = [];
          messagePairs.forEach((pair, index) => {
            // Build a stable base id for this exchange so it stays
            // consistent across refetches/rerenders. Prefer backend run_id
            // (e.g. "ai_thread_...") and fall back to thread_id+index.
            const baseId =
              (pair.run_id && String(pair.run_id)) ||
              `${pair.thread_id}_${index}`;

            // Add user message first
            if (pair.message) {
              transformedMessages.push({
                id: `user_${baseId}`,
                text: pair.message,
                sender: "user" as const,
                timestamp: new Date(pair.created_at),
                run_id: pair.run_id,
                isVoice: !!pair.audio_url && pair.audio_url.trim() !== "",
                voiceUrl:
                  pair.audio_url && pair.audio_url.trim() !== ""
                    ? pair.audio_url
                    : undefined,
              });
            }

            // Add AI response second (model-generated)
            // Use created_at + a small offset for AI response timestamp (since there's no updated_at in this API format)
            if (pair.response) {
              const aiTimestamp = new Date(pair.created_at);
              // Add 1 second offset to ensure AI response comes after user message
              aiTimestamp.setSeconds(aiTimestamp.getSeconds() + 1);

              // Extract confidence: prioritize max_confidence over avg_confidence
              const confidenceValue =
                (pair as any).max_confidence !== undefined
                  ? (pair as any).max_confidence
                  : (pair as any).maxConfidence !== undefined
                    ? (pair as any).maxConfidence
                    : (pair as any).avg_confidence !== undefined
                      ? (pair as any).avg_confidence
                      : (pair as any).average_confidence !== undefined
                        ? (pair as any).average_confidence
                        : (pair as any).avgConfidence !== undefined
                          ? (pair as any).avgConfidence
                          : (pair as any).confidence !== undefined
                            ? (pair as any).confidence
                            : ((pair as any).confidence_score ?? null);

              transformedMessages.push({
                id: `ai_${baseId}`,
                text: pair.response,
                sender: "ai" as const,
                timestamp: aiTimestamp,
                run_id: pair.run_id,
                confidence: confidenceValue,
                fromTalent: false,
              });
            }

            // If backend has a real talent_response for this run,
            // append it as a distinct message, marked as fromTalent=true.
            // Check both snake_case and camelCase variants
            const talentResponse =
              (pair as any).talent_response ??
              (pair as any).talentResponse ??
              (pair as any).talent_reply ??
              (pair as any).talentReply;

            // Debug logging to help troubleshoot
            if (index === 0) {
              console.log(
                "🔍 [useGetAITwinConversationByThreadId] Checking for talent_response:",
                {
                  pairKeys: Object.keys(pair),
                  talent_response: (pair as any).talent_response,
                  talentResponse: (pair as any).talentResponse,
                  found: !!talentResponse,
                },
              );
            }

            if (talentResponse && String(talentResponse).trim() !== "") {
              console.log(
                "✅ [useGetAITwinConversationByThreadId] Found talent_response, creating message:",
                {
                  run_id: pair.run_id,
                  talentResponse: String(talentResponse).substring(0, 50),
                },
              );
              const talentTs = new Date(pair.created_at);
              // Place it after the model response
              talentTs.setSeconds(talentTs.getSeconds() + 2);
              transformedMessages.push({
                id: `talent_${baseId}`,
                text: String(talentResponse),
                sender: "ai" as const,
                timestamp: talentTs,
                run_id: pair.run_id,
                fromTalent: true,
              });
            }
          });

          // Sort messages by timestamp to ensure chronological order
          transformedMessages.sort((a, b) => {
            const timeA =
              a.timestamp instanceof Date
                ? a.timestamp.getTime()
                : new Date(a.timestamp).getTime();
            const timeB =
              b.timestamp instanceof Date
                ? b.timestamp.getTime()
                : new Date(b.timestamp).getTime();
            return timeA - timeB;
          });

          // Extract user and talent info from first pair
          const userName =
            firstPair.user?.first_name && firstPair.user?.last_name
              ? `${firstPair.user.first_name} ${firstPair.user.last_name}`.trim()
              : firstPair.user?.username || "User";
          const userAvatar = firstPair.user?.profile_image;

          return {
            ...result.data,
            data: {
              id: firstPair.thread_id,
              thread_id: firstPair.thread_id,
              talentId: firstPair.talent?.id || 0,
              userId: firstPair.user?.id || 0,
              userName: userName,
              userAvatar: userAvatar,
              messages: transformedMessages,
              lastMessageAt:
                transformedMessages.length > 0
                  ? transformedMessages[transformedMessages.length - 1]
                      .timestamp
                  : new Date(lastPair.created_at),
              createdAt: new Date(firstPair.created_at),
            } as AITwinConversation,
          };
        })()
      : undefined;

  return {
    ...result,
    data: transformedData,
  };
};

/**
 * Backend API endpoint for talent interrupting an AI Twin run
 * POST /api/v1/talents/chat/interrupt
 * Requires header: talent-id
 */
const interruptAITwinConversationEndpoint = () => "/talents/chat/interrupt";

/** Params: talent context = headers["talent-id"]; CTP context = no talent-id (backend uses business_id in body) */
export const useInterruptAITwinConversation = createPostApiHook<
  InterruptAITwinPayload,
  { headers?: { "talent-id"?: string } },
  InterruptAITwinResponse
>(interruptAITwinConversationEndpoint, {
  isFormDataMethod: false,
});

/**
 * Custom hook that only uses backend API - no fallback to static/dummy data
 * talent_id is REQUIRED per API documentation
 */
export const useGetAITwinConversations = (options: {
  key: any[];
  params: {
    talentId: number;
    limit?: number;
    offset?: number;
    business_id?: number | string | null;
    ait_card_request_id?: number | string | null;
  };
  options?: any;
}) => {
  const { key, params, options: queryOptions } = options;
  const {
    talentId,
    limit = 10,
    offset = 0,
    business_id,
    ait_card_request_id,
  } = params;

  // Validate that talentId is provided and valid (talent_id is required)
  const isValidTalentId = talentId && talentId > 0;

  // Only use backend API - no fallback to localStorage or dummy data
  return useGetAITwinConversationsBackend({
    key: [...key, "backend", business_id ?? "", ait_card_request_id ?? ""],
    params: { talentId, limit, offset, business_id, ait_card_request_id },
    options: {
      ...queryOptions,
      retry: queryOptions?.retry ?? 2,
      // Disable query if talentId is invalid (talent_id is required)
      enabled: queryOptions?.enabled !== false && isValidTalentId,
    },
  });
};

/**
 * Backend API endpoint for getting user conversations
 * Uses the same endpoint as talent conversations but with user_id parameter
 * GET /api/v1/talents/list/conversations?user_id={userId}&limit=10&offset=0
 * Note: For users, we pass user_id parameter
 */
const getUserAITwinConversationsEndpoint = ({
  userId,
  limit = 10,
  offset = 0,
}: {
  userId: number;
  limit?: number;
  offset?: number;
}) => {
  // Validate that userId is provided and valid
  if (!userId || userId <= 0) {
    throw new Error("user_id is required and must be a positive number");
  }

  const params = new URLSearchParams();
  params.set("user_id", String(userId)); // Required parameter for user conversations
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  return `/talents/list/conversations?${params.toString()}`;
};

/**
 * User conversation list item from API (without messages)
 * Uses the same structure as talent conversations but shows talent info instead of user info
 */
export interface UserAITwinConversationListItem {
  thread_id: string;
  talent_id: number;
  last_id: number;
  last_message_at: string;
  user_id?: number; // Some API responses may still use user_id for talent id
  talent?: {
    id: number;
    username?: string;
    first_name?: string;
    last_name?: string;
    profile_image?: string;
    cover_image?: string;
    bio?: string;
    twin?: {
      name?: string;
      language?: string;
    };
  };
  // Backward compatibility: some responses may still include talent info under `user`
  user?: {
    id: number;
    first_name?: string;
    last_name?: string;
    username?: string;
    profile_image?: string;
  };
}

/**
 * Backend API hook for listing user conversations (raw)
 * Uses the same endpoint as talent conversations but with user_id parameter
 */
const useGetUserAITwinConversationsBackendRaw = createGetApiHook<
  APIResponseType<UserAITwinConversationListItem[]>,
  { userId: number; limit?: number; offset?: number }
>(getUserAITwinConversationsEndpoint);

/**
 * Transform API response to AITwinConversation format for users
 * The API returns the same structure, but we need to extract talent info instead of user info
 */
const useGetUserAITwinConversationsBackend = (options: {
  key: any[];
  params: { userId: number; limit?: number; offset?: number };
  options?: any;
}) => {
  const result = useGetUserAITwinConversationsBackendRaw({
    key: options.key,
    params: {
      userId: options.params.userId,
      limit: options.params.limit,
      offset: options.params.offset,
    },
    options: options.options,
  });

  // Transform API response to AITwinConversation format
  // Note: For users, the API response structure is the same as talent conversations
  // The `user` field in the response contains the talent/AI Twin information
  // The `user_id` field in the response is the talent_id
  const transformedData: APIResponseType<AITwinConversation[]> | undefined =
    result.data?.data
      ? {
          ...result.data,
          data: result.data.data
            .map((item: UserAITwinConversationListItem) => {
              // Determine talent (AI Twin) info depending on API shape
              const talentFromUser = item.user;
              const talentFromNested = item.talent;

              const talentId =
                item.talent_id ||
                talentFromNested?.id ||
                talentFromUser?.id ||
                item.user_id ||
                0;

              const combinedNameFromNested =
                talentFromNested?.first_name && talentFromNested?.last_name
                  ? `${talentFromNested.first_name} ${talentFromNested.last_name}`.trim()
                  : talentFromNested?.username;

              const talentName =
                talentFromNested?.twin?.name ||
                combinedNameFromNested ||
                (talentFromUser?.first_name && talentFromUser?.last_name
                  ? `${talentFromUser.first_name} ${talentFromUser.last_name}`.trim()
                  : talentFromUser?.username) ||
                "AI Twin";

              const talentAvatar =
                talentFromNested?.profile_image ||
                talentFromUser?.profile_image;

              const lastMessagePreview =
                (item as any).last_message || (item as any).message || "";

              return {
                id: item.thread_id,
                thread_id: item.thread_id,
                talentId,
                userId: options.params.userId || 0,
                userName: talentName,
                userAvatar: talentAvatar,
                messages: undefined,
                lastMessageAt: item.last_message_at,
                createdAt: item.last_message_at,
                lastMessagePreview,
              };
            })
            .filter(
              (conv) => conv.talentId && conv.talentId > 0,
            ) as AITwinConversation[],
        }
      : undefined;

  return {
    ...result,
    data: transformedData,
  };
};

/**
 * Custom hook for fetching user conversations
 * Uses the same API endpoint as talent conversations but with user_id parameter
 * user_id is REQUIRED per API documentation
 */
export const useGetUserAITwinConversations = (options: {
  key: any[];
  params: { userId: number; limit?: number; offset?: number };
  options?: any;
}) => {
  const { key, params, options: queryOptions } = options;
  const { userId, limit = 10, offset = 0 } = params;

  // Validate that userId is provided and valid (user_id is required)
  const isValidUserId = userId && userId > 0;

  // Only use backend API - no fallback to localStorage or dummy data
  return useGetUserAITwinConversationsBackend({
    key: [...key, "backend"],
    params: { userId, limit, offset },
    options: {
      ...queryOptions,
      retry: queryOptions?.retry ?? 2,
      // Disable query if userId is invalid (user_id is required)
      enabled: queryOptions?.enabled !== false && isValidUserId,
    },
  });
};
